import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api/apiClient';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Star, TrendingUp, Zap, Clock, RefreshCw, Loader2, ExternalLink, Filter, BarChart2, CheckCircle2 } from 'lucide-react';

const LEVEL_CONFIG = {
  NOT_READY:  { label: 'Not Ready',  color: 'text-muted-foreground',                    bg: 'bg-muted/50',                                                               border: 'border-border',                                  icon: Clock     },
  WARMING:    { label: 'Warming',    color: 'text-amber-600 dark:text-amber-400',        bg: 'bg-amber-50 dark:bg-amber-950/20',                                          border: 'border-amber-200 dark:border-amber-800',         icon: TrendingUp},
  NEAR_READY: { label: 'Near Ready', color: 'text-blue-600 dark:text-blue-400',          bg: 'bg-blue-50 dark:bg-blue-950/20',                                            border: 'border-blue-200 dark:border-blue-800',           icon: Zap       },
  READY:      { label: 'MQL Ready',  color: 'text-emerald-600 dark:text-emerald-400',    bg: 'bg-emerald-50 dark:bg-emerald-950/20',                                      border: 'border-emerald-200 dark:border-emerald-800',     icon: Star      },
  QUALIFIED:  { label: 'Qualified',  color: 'text-violet-600 dark:text-violet-400',      bg: 'bg-violet-50 dark:bg-violet-950/20',                                        border: 'border-violet-200 dark:border-violet-800',       icon: CheckCircle2 },
} as const;

const ScoreBar = ({ score, level }: { score: number; level: string }) => {
  const colors: Record<string, string> = { NOT_READY: 'bg-muted', WARMING: 'bg-amber-400', NEAR_READY: 'bg-blue-500', READY: 'bg-emerald-500' };
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colors[level] || 'bg-muted'}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-mono font-bold text-foreground w-8 text-right">{score}</span>
    </div>
  );
};

export default function MQLReadinessDashboard() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData]         = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState<string>('ALL');
  const [minScore, setMinScore] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'ALL' && filter !== 'QUALIFIED') params.set('level', filter);
      if (minScore) params.set('minScore', minScore);
      const { data: d } = await apiClient.get(`/api/engage/scores?${params}`);
      setData(d.data || []);
    } catch { setData([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter, minScore]);

  const counts = data.reduce((acc, r) => { acc[r.mqlLevel] = (acc[r.mqlLevel] || 0) + 1; return acc; }, {} as Record<string, number>);
  const readyCount     = counts['READY']      || 0;
  const nearReadyCount = counts['NEAR_READY'] || 0;
  const warmingCount   = counts['WARMING']    || 0;
  const qualifiedCount = data.filter(r => r.mqlQualified).length;

  const submitToMQL = async (row: any) => {
    try {
      // Get pre-fill from journey
      const { data: prefill } = await apiClient.get(`/api/mql/prefill/${row.contactId}`);
      const authData = localStorage.getItem('auth-storage');
      const userId = authData ? JSON.parse(authData)?.state?.user?.id || '' : '';
      await apiClient.post('/api/mql', {
        accountId:   prefill.accountId,
        contactId:   prefill.contactId,
        contactName: prefill.contactName,
        score:       prefill.score,
        notes:       prefill.notes,
        criteria:    prefill.criteria,
        submittedById: userId,
      });
      addToast({ type: 'success', title: 'MQL Submitted', description: `${row.contactName} submitted to MQL Gate with score ${prefill.score}` });
    } catch (e: any) {
      addToast({ type: 'error', title: 'Submit failed', description: e.response?.data?.error || e.message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="MQL Readiness"
        description="ABM maturity scores computed from magnet interactions, self-assessments & journey depth"
        icon={TrendingUp}
        actions={
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm text-muted-foreground transition-colors">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </button>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { level: 'QUALIFIED',  count: qualifiedCount, label: 'Qualified MQL', desc: 'Qual 60+ & engaged' },
          { level: 'READY',      count: readyCount,     label: 'MQL Ready',   desc: 'Score 75+' },
          { level: 'NEAR_READY', count: nearReadyCount, label: 'Near Ready',  desc: 'Score 55–74' },
          { level: 'WARMING',    count: warmingCount,   label: 'Warming Up',  desc: 'Score 30–54' },
          { level: 'ALL',        count: data.length,    label: 'Total Tracked', desc: 'All contacts' },
        ].map(s => {
          const cfg = LEVEL_CONFIG[s.level as keyof typeof LEVEL_CONFIG] || LEVEL_CONFIG.NOT_READY;
          const Icon = cfg?.icon || BarChart2;
          return (
            <button key={s.level} onClick={() => setFilter(s.level)}
              className={`p-4 rounded-xl border text-left transition-all hover:shadow-sm ${
                filter === s.level ? `${cfg?.bg || 'bg-muted/50'} ${cfg?.border || 'border-border'} ring-2 ring-primary/20` : 'bg-card border-border hover:border-primary/30'
              }`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`h-4 w-4 ${cfg?.color || 'text-muted-foreground'}`} />
                <span className={`text-2xl font-bold ${cfg?.color || 'text-foreground'}`}>{s.count}</span>
              </div>
              <p className="text-sm font-semibold text-foreground">{s.label}</p>
              <p className="text-xs text-muted-foreground">{s.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          {(['ALL','QUALIFIED','READY','NEAR_READY','WARMING','NOT_READY'] as const).map(l => (
            <button key={l} onClick={() => setFilter(l)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                filter === l ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {l === 'ALL' ? 'All' : LEVEL_CONFIG[l]?.label}
              {l === 'QUALIFIED' ? (qualifiedCount ? <span className={`ml-1 px-1 py-0.5 rounded-full text-[10px] font-bold ${filter === l ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{qualifiedCount}</span> : null)
                : (counts[l] ? <span className={`ml-1 px-1 py-0.5 rounded-full text-[10px] font-bold ${filter === l ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{counts[l]}</span> : null)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <input type="number" placeholder="Min score" value={minScore}
            onChange={e => setMinScore(e.target.value)}
            className="w-24 px-2 py-1 rounded-lg border border-border bg-background text-xs" />
        </div>
      </div>

      {/* Score explanation */}
      <div className="p-3 rounded-xl bg-muted/30 border border-border text-xs text-muted-foreground flex flex-wrap gap-4">
        <span><strong className="text-foreground">ABM Score = </strong>Stage depth (40) + Event richness (30) + Self-assessment (20) + Calls (10)</span>
        <span><strong className="text-foreground">Qual Score = </strong>Need (30) + MES-fit (25) + Authority (20) + Timing (15) + Budget (10)</span>
        <span><strong className="text-violet-600 dark:text-violet-400">Qualified MQL = </strong>Qual ≥ 60 & engagement Near-Ready+ (or Stage-14 "Qualified")</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
          No engagement data yet — interactions with magnet tracking links will appear here automatically.
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Contact</th>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-left">ABM Score</th>
                <th className="px-4 py-3 text-left">Qual Score</th>
                <th className="px-4 py-3 text-left">Readiness</th>
                <th className="px-4 py-3 text-left">Self-Assessed Band</th>
                <th className="px-4 py-3 text-left">Stages Engaged</th>
                <th className="px-4 py-3 text-left">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(filter === 'QUALIFIED' ? data.filter(r => r.mqlQualified) : data).map(row => {
                const cfg = LEVEL_CONFIG[row.mqlLevel as keyof typeof LEVEL_CONFIG] || LEVEL_CONFIG.NOT_READY;
                const Icon = cfg.icon;
                return (
                  <tr key={row.contactId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{row.contactName}</p>
                      {row.email && <p className="text-xs text-muted-foreground">{row.email}</p>}
                      {row.title && <p className="text-xs text-muted-foreground">{row.title}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{row.accountName}</p>
                      <p className="text-xs text-muted-foreground">{row.industry} · {row.region}</p>
                    </td>
                    <td className="px-4 py-3 w-36">
                      <ScoreBar score={row.score} level={row.mqlLevel} />
                    </td>
                    <td className="px-4 py-3 w-32">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono font-bold text-foreground">{row.qualScore ?? 0}</span>
                        {row.mqlQualified && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400">
                            <CheckCircle2 className="h-2.5 w-2.5" />MQL
                          </span>
                        )}
                        {row.mqlDecision === 'disqualified' && (
                          <span className="text-[10px] font-medium text-muted-foreground">DQ</span>
                        )}
                      </div>
                      {row.dealSizeUsd ? <p className="text-[10px] text-muted-foreground mt-0.5">${Number(row.dealSizeUsd).toLocaleString()}</p> : null}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.color}`}>
                        <Icon className="h-2.5 w-2.5" />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.selfAssessedBand
                        ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">{row.selfAssessedBand}</span>
                        : <span className="text-xs text-muted-foreground">Not assessed</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground font-mono">{row.breakdown?.journeyStagesCompleted ?? row.stagesEngaged ?? 0}/14</span>
                    </td>
                    <td className="px-4 py-3">
                      {row.updatedAt && <span className="text-xs text-muted-foreground">{new Date(row.updatedAt).toLocaleDateString('en-IN')}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/abm-journeys/${row.contactId}`)}
                          className="flex items-center gap-1 text-xs text-primary hover:underline">
                        Journey <ExternalLink className="h-2.5 w-2.5" />
                      </button>
                      {(row.mqlLevel === 'READY' || row.mqlLevel === 'NEAR_READY' || (row.mqlLevel === 'WARMING' && (row.breakdown?.journeyStagesCompleted ?? 0) >= 5)) && (
                        <button onClick={() => submitToMQL(row)}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-semibold">
                          → MQL Gate
                        </button>
                      )}
                    </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
